import { LOCALE_PREFIXES } from "@/i18n/locales"
import { ROUTES } from "@/lib/routes"

export type NavigationScreen = "in-page" | "push" | "replace" | "sheet" | "native" | "external"

export interface NavigationRule {
  patterns: string[]
  screen: NavigationScreen
  header?: "standard" | "search" | "none"
  route?: string
  prefetch?: boolean
}

export interface NavigationRuleFile {
  version: number
  disabled: boolean
  rules: NavigationRule[]
}

export const NAVIGATION_RULE_FILE_CACHE_CONTROL = "public, max-age=3600, s-maxage=86400, stale-while-revalidate=604800"

const localePrefixGroup = `(?:${Object.values(LOCALE_PREFIXES)
  .filter((prefix): prefix is string => prefix !== undefined)
  .join("|")})?`

// The region segment is optional: the default region is served at the root (/productos/…)
// and the app may still hold URLs from before that (/mx/productos/…). Both have to match,
// including while an app runs on a rule file cached from either side of the change.
const region = (rest: string) => `^${localePrefixGroup}(?:/[a-z]{2})?${rest}$`

// What the store serves at the root that isn't a page: the proxy matcher's exemptions, the
// public asset folders, the SSO routes and the BNPL entry (/checkout?checkoutToken=…), which
// the proxy redirects to /checkout/<token>.
const NOT_A_PAGE = ["api", "_next", "auth", "images", "videos", "loading", "\\.?well-known", "favicon\\.ico", "sitemap\\.xml", "robots\\.txt", "checkout(?=[?#]|$)"].join("|")

// The catch-all keeps the exact reach it had when every page sat under /<cc>/, plus root-level
// pages: a first segment that is neither a bare region (/mx?x) nor one of the above, and never
// the root with a query (/?signup=1), which the root rule leaves unmatched.
const ANY_PAGE = [`^${localePrefixGroup}/[a-z]{2}/.+$`, `^${localePrefixGroup}/(?![a-z]{2}(?:[/?#]|$))(?!(?:${NOT_A_PAGE})(?:[/?#]|$))[^/?#].*$`]

export const NAVIGATION_RULE_FILE: NavigationRuleFile = {
  version: 1,
  disabled: false,
  rules: [
    { patterns: [region("/?")], screen: "in-page" },
    { patterns: [region(`/${ROUTES.CART}(?:\\?.*)?`)], screen: "sheet" },
    { patterns: [region("/checkout/[^/]+")], screen: "external" },
    { patterns: [region(`/${ROUTES.ACCOUNT}`)], screen: "native", route: "account" },
    { patterns: [region(`/${ROUTES.SEARCH}(?:\\?.*)?`)], screen: "push", header: "search", prefetch: true },
    { patterns: [region(`/${ROUTES.PRODUCTS}/[^/]+`), region("/products/[^/]+")], screen: "push", header: "standard", prefetch: true },
    {
      patterns: [region(`/${ROUTES.COLLECTIONS}/[^/]+`), region(`/${ROUTES.COLLECTIONS}`)],
      screen: "push",
      header: "standard",
      prefetch: true,
    },
    { patterns: [region(`/${ROUTES.CATEGORIES}(?:/.*)?`), region("/categories/.+")], screen: "push", header: "standard", prefetch: true },
    { patterns: [region(`/${ROUTES.WISHLIST}`)], screen: "push", header: "standard", prefetch: true },
    {
      patterns: [
        region(`/(?:${ROUTES.SIGN_IN}|${ROUTES.SIGN_UP}|${ROUTES.CHECKOUT}|${ROUTES.VERIFY})(?:/.*)?`),
        region(`/(?:${ROUTES.SIGN_IN}|${ROUTES.SIGN_UP}|${ROUTES.CHECKOUT})(?:\\?.*)?`),
        region("/enlace(?:/.*)?"),
        region("/continuar-compra/[^/]+"),
      ],
      screen: "push",
      header: "none",
    },
    { patterns: [region(`/(?:legal|${ROUTES.HOW_TO_BUY}|${ROUTES.FOR_MERCHANTS})(?:/.*)?`)], screen: "push", header: "standard" },
    { patterns: [region("/(?:showcase|studio)(?:/.*)?")], screen: "push", header: "standard" },
    {
      patterns: [
        "^https://(?:a\\.nelo\\.mx|wa\\.me|api\\.whatsapp\\.com|www\\.instagram\\.com|www\\.facebook\\.com|www\\.tiktok\\.com|www\\.youtube\\.com|x\\.com|apps\\.apple\\.com|play\\.google\\.com|www\\.nelo\\.mx)/",
      ],
      screen: "external",
    },
    { patterns: ["^mailto:", "^tel:"], screen: "external" },
    { patterns: ANY_PAGE, screen: "push", header: "standard" },
  ],
}

export const resolveNavigationRule = (url: string): NavigationRule | undefined =>
  NAVIGATION_RULE_FILE.rules.find((rule) => rule.patterns.some((pattern) => new RegExp(pattern).test(url)))
